import { useState, useEffect, useRef, useCallback } from 'react'
import { useAuth } from './useAuth'
import { isDemoMode } from '../demo/demoMode'
import { demoDrillResultStream } from '../demo/demoData'

export interface WebSocketMessage {
  type:
    | 'join_room'
    | 'leave_room'
    | 'drill_result'
    | 'event_update'
    | 'athlete_update'
    | 'error'
    | 'ping'
  eventId?: string
  athleteId?: string
  data?: any
  timestamp?: string
}

export interface DrillResultUpdate {
  id: string
  value: number
  unit: string
  athlete: {
    id: string
    name: string
  }
  drill: {
    id: string
    name: string
    sport: string
  }
  timestamp: string
}

export interface UseWebSocketOptions {
  eventId?: string
  autoReconnect?: boolean
  reconnectInterval?: number
}

export interface UseWebSocketReturn {
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  lastMessage: WebSocketMessage | null
  drillResults: DrillResultUpdate[]
  sendMessage: (message: WebSocketMessage) => void
  joinEvent: (eventId: string) => void
  leaveEvent: (eventId: string) => void
  clearError: () => void
  reconnect: () => void
}

// Same origin in every environment: production's nginx and the Vite dev
// server both proxy /ws to the backend. Development used to dial
// ws://localhost:3001 directly, bypassing the proxy — on a machine where 3001
// is not this backend (another project, or a worktree stack on another port)
// the live-results panel showed "Connection lost" forever.
const WS_URL = `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}/ws`

function useDemoWebSocket(isActive: boolean): UseWebSocketReturn {
  const [drillResults, setDrillResults] = useState<DrillResultUpdate[]>(() =>
    isActive ? demoDrillResultStream() : []
  )

  // Drip in a fresh "live" result periodically so the feed feels real.
  // Only runs in demo mode — no timers/state churn for real users.
  useEffect(() => {
    if (!isActive) {
      setDrillResults([])
      return
    }

    setDrillResults(demoDrillResultStream())
    const pool = demoDrillResultStream()
    let i = 0
    const interval = setInterval(() => {
      const next = pool[i % pool.length]
      setDrillResults(prev =>
        [{ ...next, id: `live-${Date.now()}`, timestamp: new Date().toISOString() }, ...prev].slice(
          0,
          50
        )
      )
      i += 1
    }, 4000)
    return () => clearInterval(interval)
  }, [isActive])

  return {
    isConnected: true,
    isConnecting: false,
    error: null,
    lastMessage: null,
    drillResults,
    sendMessage: () => {},
    joinEvent: () => {},
    leaveEvent: () => {},
    clearError: () => {},
    reconnect: () => {},
  }
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  const { user, token } = useAuth()
  const { eventId, autoReconnect = true, reconnectInterval = 5000 } = options
  const demoActive = isDemoMode()
  // Hooks must run unconditionally; in demo mode we ignore the real socket below.
  const demo = useDemoWebSocket(demoActive)

  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null)
  const [drillResults, setDrillResults] = useState<DrillResultUpdate[]>([])

  const websocket = useRef<WebSocket | null>(null)
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null)
  const isManualDisconnect = useRef(false)

  const connect = useCallback(() => {
    if (websocket.current?.readyState === WebSocket.OPEN) {
      return
    }

    setIsConnecting(true)
    setError(null)
    isManualDisconnect.current = false

    try {
      websocket.current = new WebSocket(WS_URL)

      websocket.current.onopen = () => {
        console.log('WebSocket connected')
        setIsConnected(true)
        setIsConnecting(false)
        setError(null)

        // Auto-join event room if eventId provided
        if (eventId && user && token) {
          sendMessage({
            type: 'join_room',
            eventId,
            data: { token },
          })
        }
      }

      websocket.current.onmessage = event => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          setLastMessage(message)

          switch (message.type) {
            case 'drill_result':
              if (message.data) {
                setDrillResults(prev => [message.data, ...prev].slice(0, 50)) // Keep last 50 results
              }
              break

            case 'error':
              setError(message.data?.error || 'WebSocket error')
              break

            case 'ping':
              // Respond to ping with pong
              if (message.data?.status === 'connected') {
                console.log('WebSocket ping received, connection established')
              }
              break

            default:
              console.log('WebSocket message received:', message)
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err)
          setError('Failed to parse server message')
        }
      }

      websocket.current.onclose = event => {
        console.log('WebSocket disconnected:', event.code, event.reason)
        setIsConnected(false)
        setIsConnecting(false)

        // Auto-reconnect if not manually disconnected
        if (autoReconnect && !isManualDisconnect.current && event.code !== 1000) {
          setError('Connection lost. Attempting to reconnect...')
          reconnectTimeout.current = setTimeout(() => {
            connect()
          }, reconnectInterval)
        }
      }

      websocket.current.onerror = event => {
        console.error('WebSocket error:', event)
        setError('WebSocket connection error')
        setIsConnecting(false)
      }
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err)
      setError('Failed to establish connection')
      setIsConnecting(false)
    }
  }, [eventId, user, token, autoReconnect, reconnectInterval])

  const disconnect = useCallback(() => {
    isManualDisconnect.current = true

    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current)
      reconnectTimeout.current = null
    }

    if (websocket.current) {
      websocket.current.close(1000, 'Manual disconnect')
      websocket.current = null
    }

    setIsConnected(false)
    setIsConnecting(false)
  }, [])

  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (websocket.current?.readyState === WebSocket.OPEN) {
      websocket.current.send(
        JSON.stringify({
          ...message,
          timestamp: new Date().toISOString(),
        })
      )
    } else {
      setError('WebSocket is not connected')
    }
  }, [])

  const joinEvent = useCallback(
    (eventId: string) => {
      sendMessage({
        type: 'join_room',
        eventId,
        data: { token },
      })
    },
    [sendMessage, token]
  )

  const leaveEvent = useCallback(
    (eventId: string) => {
      sendMessage({
        type: 'leave_room',
        eventId,
      })
    },
    [sendMessage]
  )

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const reconnect = useCallback(() => {
    disconnect()
    setTimeout(connect, 100)
  }, [connect, disconnect])

  // Connect on mount and when dependencies change (skipped entirely in demo mode)
  useEffect(() => {
    if (demoActive) {
      return
    }
    if (user && token) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [connect, disconnect, user, token, demoActive])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeout.current) {
        clearTimeout(reconnectTimeout.current)
      }
      disconnect()
    }
  }, [disconnect])

  if (demoActive) {
    return demo
  }

  return {
    isConnected,
    isConnecting,
    error,
    lastMessage,
    drillResults,
    sendMessage,
    joinEvent,
    leaveEvent,
    clearError,
    reconnect,
  }
}
